import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../App';
import { isTrialJustExpired } from '../../utils/trialHelpers';
import Modal from './Modal';

/**
 * TrialExpiredModal - One-time modal shown when trial expires
 * Only shows once per session using localStorage
 */
const TrialExpiredModal = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    // Check if trial just expired
    const trialExpired = isTrialJustExpired(user);
    
    if (trialExpired && user) {
      // Check if modal was already shown in this session
      const modalShownKey = `trial_expired_modal_shown_${user.id}`;
      const alreadyShown = sessionStorage.getItem(modalShownKey);
      
      if (!alreadyShown) {
        setIsOpen(true);
        sessionStorage.setItem(modalShownKey, 'true');
      }
    }
  }, [user]);

  const handleUpgrade = () => {
    setIsOpen(false);
    navigate('/payment');
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <div className="text-center py-6 px-4">
        {/* Icon */}
        <div className="mb-6">
          <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center mx-auto">
            <span className="text-4xl">✨</span>
          </div>
        </div>

        {/* Title */}
        <h2 className="text-3xl font-bold text-gray-900 mb-3">
          Your Trial Has Ended
        </h2>

        {/* Message */}
        <p className="text-gray-600 mb-2 text-lg">
          We hope you enjoyed the Pro features!
        </p>
        <p className="text-gray-600 mb-6">
          You can continue using the app with Free plan limits, or upgrade to Pro to unlock:
        </p>

        {/* Pro Features List */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 mb-6 text-left">
          <ul className="space-y-2 text-sm text-gray-700">
            <li className="flex items-center gap-2">
              <span className="text-green-500">✓</span>
              <span><strong>Unlimited tasks</strong> & contacts</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-green-500">✓</span>
              <span><strong>Task reminders</strong> (1 hour before)</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-green-500">✓</span>
              <span><strong>Recurring tasks</strong> automation</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-green-500">✓</span>
              <span><strong>Archive</strong> completed tasks</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-green-500">✓</span>
              <span><strong>Email digests</strong> (daily & weekly)</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-green-500">✓</span>
              <span><strong>2GB storage</strong> & priority support</span>
            </li>
          </ul>
        </div>

        {/* Pricing */}
        <div className="mb-6">
          <p className="text-2xl font-bold text-gray-900 mb-1">
            Just $3.99/month
          </p>
          <p className="text-sm text-gray-500">or $39/year (save 18%)</p>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={handleClose}
            className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
          >
            Continue with Free
          </button>
          <button
            onClick={handleUpgrade}
            className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-medium hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl"
          >
            Upgrade to Pro
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default TrialExpiredModal;
